import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import {
  DataGridPro,
  GridAutosizeOptions,
  GridFilterModel,
  GridLoadingOverlay,
  useGridApiContext,
  GridSortModel,
} from "@mui/x-data-grid-pro";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  debounce,
  Grid,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { useLeaseLoader } from "services/DashboardService";
import dashboardViewsColumns from "./dashboardViewsColumns.json";
import { FilterModel, Lease, LeaseFilters } from "../../all_Interfaces";
import { log } from "util";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import TaskIcon from "@mui/icons-material/Task";
import Stack from "@mui/material/Stack";
import { getLeaseData } from "../../redux/slices/leaseByIdSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  GridToolbarExport,
  GridToolbarContainer,
  GridCsvExportOptions,
} from "@mui/x-data-grid-pro";
import { alignProperty } from "@mui/material/styles/cssUtils";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableViewIcon from "@mui/icons-material/TableView";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
//import { ExportCsv } from '@mui/x-data-grid-pro';

interface DashboardDataGridProps {
  selectedTab?: string;
  isActive?: boolean;
}

interface CustomDropDownHeaderFilterProps {
  item: any;
  dbFilters: any;
  onFilterChange: (field: string, value: any) => void;
  getValues: (data: any[]) => string[];
  dbValues: any;
}

interface CustomTextHeaderFilterProps {
  item: any;
  onFilterChange: (field: string, value: string) => void;
}

const DashboardDataGrid: React.FC<DashboardDataGridProps> = (props) => {
  const { loadLeasesByView } = useLeaseLoader();
  const dispatch = useAppDispatch();
  const userRole = useAppSelector((state) =>
    state.userContextDetails?.data[0]?.role.toLowerCase()
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const navigate = useNavigate();
  const [viewTab, setViewTab] = useState<number>(0);
  const [valueDb, setValueDb] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState<number>(25);
  const [rowData, setRowData] = useState<Lease[]>([]);
  const [dbFilters, setDbFilters] = useState<LeaseFilters>({
    leaseStatus: [],
    investmentId: [],
    investmentName: [],
    propertyName: [],
    propertyId: [],
    leaseCommencement: [],
    rentCommencement: [],
    expiration: [],
    nuveenSigningDate: [],
    propMgrFullName: [],
    secPropMgrFullName: [],
    dashboardTopFilters: [],
  });
  const [rowSize, setRowSize] = useState<number>();
  const [dbTopFiltersCount, setDbTopFiltersCount] = useState<Array<any>>([]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: pageSize,
  });

  const [filterModel, setFilterModel] = useState<any>({
    first: 0,
    rows: pageSize,
    sortOrder: 1,
    filters: [],
    globalFilter: null,
  });

  const blankFilters: FilterModel = {
    first: 0,
    rows: pageSize,
    sortOrder: 1,
    filters: [],
    globalFilter: null,
    sortField: "",
  };

  useEffect(() => {
    GetDashboardData(blankFilters, 0);
  }, []);

  const actionColumn = {
    field: "actions",
    headerName: "Actions",
    width: 120,
    sortable: false,
    filterable: false,
    headerAlign: "center",
    renderCell: (params: any) => {
      const handleEditClick = () => {
        navigate(`/lease/form/edit/${params.row.leaseId}`);
      };

      const handleViewClick = () => {
        navigate(`/lease/form/view/${params.row.leaseId}`);
      };
      const handleApprovalClick = () => {
        navigate(`/lease/form/approval/${params.row.leaseId}`);
      };

      const leaseStatus = params.row.leaseStatus;
      const isActive = params.row.isActive.toLocaleLowerCase();
      const isPrMAdmin = false;
      // show edit icon and task icon based on conditions
      const showEditIcon =
        (userRole.toLocaleLowerCase() === "admin" &&
          isActive.toLocaleLowerCase() === "inprogress") ||
        (leaseStatus.toLocaleLowerCase() === "saved" &&
          isActive.toLocaleLowerCase() === "inprogress") ||
        ((userRole.toLocaleLowerCase() == "property manager" ||
          "assistant property manager" ||
          "property mgmt accountant") &&
          isActive.toLocaleLowerCase() === "inprogress" &&
          leaseStatus.toLocaleLowerCase() === "saved");

      const showTaskIcon =
        (leaseStatus.toLocaleLowerCase() !== "saved" &&
          isActive.toLocaleLowerCase() === "inprogress") ||
        ((userRole.toLocaleLowerCase() === "accenture" || isPrMAdmin) &&
          leaseStatus.toLocaleLowerCase() !== "saved" &&
          isActive.toLocaleLowerCase() === "inprogress") ||
        userRole.toLocaleLowerCase() === "admin";

      return (
        <Stack
          direction="row"
          spacing={1}
          my={2}
        >
          <IconButton
            onClick={handleViewClick}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            aria-label="view"
          >
            <VisibilityIcon sx={{ fontSize: 16, color: "#263746" }} />
          </IconButton>
          {showEditIcon && (
            <IconButton
              onClick={handleEditClick}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              aria-label="edit"
            >
              <EditIcon sx={{ fontSize: 16, color: "#263746" }} />
            </IconButton>
          )}
          {showTaskIcon && (
            <IconButton
              onClick={handleApprovalClick}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              aria-label="approval"
            >
              <TaskIcon sx={{ fontSize: 16, color: "#263746" }} />
            </IconButton>
          )}
        </Stack>
      );
    },
  };

  const CustomDropDownHeaderFilter = (params: any) => {
    const apiRef = useGridApiContext();
    const { colDef } = params;
    const field = colDef?.field || params.field;

    const fieldData = dbFilters
      ? field === "propertyManagerName"
        ? dbFilters["propMgrFullName"]
        : field === "secondaryPropertyManagerName"
        ? dbFilters["secPropMgrFullName"]
        : dbFilters[field]
      : [];
    const columnFilterList =
      fieldData?.map((element: any) => element.value) || [];
    const [localValue, setLocalValue] = useState<string[]>(
      valueDb[field] || []
    );

    useEffect(() => {
      setLocalValue(valueDb[field] || []);
    }, [valueDb[field]]);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          paddingBottom: "10px",
        }}
      >
        <Autocomplete
          multiple
          className="autocomplete-root"
          ListboxProps={{
            className: "autocomplete-dashboard",
          }}
          limitTags={1}
          fullWidth
          onFocus={() => apiRef.current.startHeaderFilterEditMode(colDef.field)}
          onChange={(event, newValue) => {
            setLocalValue(newValue);
            handleDropdownFilterChange(field, newValue);
          }}
          value={localValue}
          options={columnFilterList}
          filterOptions={(options, { inputValue }) => {
            return options.filter((option) =>
              option.toLowerCase().includes(inputValue.toLowerCase())
            );
          }}
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={localValue.length === 0 ? "All" : ""}
              variant="standard"
              sx={{
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "14px",
                  textAlign: "center",
                  opacity: 0.7,
                },
              }}
            />
          )}
        />
      </Box>
    );
  };
  const CustomTextHeaderFilter: React.FC<CustomTextHeaderFilterProps> =
    React.memo(({ item, onFilterChange }) => {
      const apiRef = useGridApiContext();
      const [localValue, setLocalValue] = useState(valueDb[item.field] || "");

      useEffect(() => {
        setLocalValue(valueDb[item.field] || "");
      }, [valueDb[item.field]]);

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
            paddingBottom: "10px",
          }}
        >
          <TextField
            id="standard-basic"
            variant="standard"
            size="small"
            value={localValue}
            onFocus={() => apiRef.current.startHeaderFilterEditMode(item.field)}
            onChange={(event) => {
              setLocalValue(event.target.value);
              onFilterChange(item.field, event.target.value);
            }}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Box>
      );
    });

  const handleTabChange = (event: any, newValue: any) => {
    setViewTab(newValue);
    setFilterModel(blankFilters);
    setSortModel([]);
    GetDashboardData(blankFilters, newValue);
    setValueDb({});
    setDbFilters({
      leaseStatus: [],
      investmentId: [],
      investmentName: [],
      propertyName: [],
      propertyId: [],
      leaseCommencement: [],
      rentCommencement: [],
      expiration: [],
      nuveenSigningDate: [],
      propMgrFullName: [],
      secPropMgrFullName: [],
      dashboardTopFilters: [],
    });
  };

  const GetDashboardData = async (filter, tab) => {
    setLoading(true);

    try {
      const data: any = await loadLeasesByView(tab, filter);
      setLoading(false);
      setRowData(data.leases);
      setDbFilters(data.leaseFilters);

      setRowSize(data.totalRecords);
      const sortedFilters =
        userRole?.toLowerCase() === "property manager" ||
        userRole?.toLowerCase() === "admin"
          ? data.leaseFilters.dashboardTopFilters.sort((a, b) => {
              const order = [
                "Abstraction in Progress",
                "Pending Activation",
                "Pending PrM",
              ];
              const aIndex = order.indexOf(a.label);
              const bIndex = order.indexOf(b.label);
              if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              return 0;
            })
          : data.leaseFilters.dashboardTopFilters;
      setDbTopFiltersCount(sortedFilters);
      return data;
    } catch (error) {
      setLoading(false);
      console.error("Error loading dashboard data:", error);
    }
  };

  const sendDashboardRequest = (tab: number) => {
    GetDashboardData(filterModel, tab);
  };

  const ref = useRef(sendDashboardRequest);
  const viewTabRef = useRef(viewTab);

  useEffect(() => {
    ref.current = sendDashboardRequest;
  }, [filterModel]);

  useEffect(() => {
    viewTabRef.current = viewTab;
  }, [viewTab]);

  const debouncedCallback = useMemo(() => {
    const func = () => {
      ref.current(viewTabRef.current);
    };
    return debounce(func, 1000);
  }, []);

  const buttonFilters = (btn: string) => {
    switch (btn) {
      case "Abstraction in Progress":
        handleHeaderButtonFilterChange("leaseStatus", [
          "Abstraction in Progress",
        ]);
        break;
      case "Pending Activation":
        handleHeaderButtonFilterChange("leaseStatus", ["Pending Activation"]);
        break;
      case "Pending PrM":
        handleHeaderButtonFilterChange("leaseStatus", [
          "Pending PrM Review",
          "Pending PrM Updates",
        ]);
        break;
      case "Pending Accenture":
        handleHeaderButtonFilterChange("leaseStatus", [
          "Pending Abstraction",
          "Pending Accenture Updates",
        ]);
        break;
        case "Pending Accenture Updates":
        handleHeaderButtonFilterChange("leaseStatus", [          
          "Pending Accenture Updates"
        ]);
        break;
        case "Pending Abstraction":
        handleHeaderButtonFilterChange("leaseStatus", [          
          "Pending Abstraction"
        ]);
        break;
        case "Submitted to Accenture":
        handleHeaderButtonFilterChange("leaseStatus", [          
          "Pending Abstraction"
        ]);
        break;

      default:
        break;
    }
  };

  const handleHeaderButtonFilterChange = (field: string, values: any) => {
    var existingFilters = [
      ...filterModel.filters.filter((f) => f.identity !== field),
      { identity: field, matchMode: "in", value: values },
    ];

    var filters = { ...filterModel, filters: existingFilters };

    GetDashboardData(filters, viewTab);
    setFilterModel(filters);
    setValueDb((prev) => ({ ...prev, [field]: values }));
  };

  const handleClearFilters = () => {
    setFilterModel(blankFilters);
    setSortModel([]);
    setValueDb({});
    GetDashboardData(blankFilters, viewTab);
  };

  const handleDropdownFilterChange = (field: string, values: any) => {
    setValueDb((prev) => ({ ...prev, [field]: values }));
    
    const filterIdentity = field === "propertyManagerName" 
      ? "propertyManagerEmailId" 
      : field === "secondaryPropertyManagerName" 
      ? "secondaryPropertyManagerEmailId" 
      : field;
    
    if (values.length === 0) {
      setFilterModel((prev) => ({
        ...prev,
        filters: [...prev.filters.filter((f) => f.identity !== filterIdentity && f.identity !== field)],
        first: 0,
        rows: pageSize,
      }));
    } else {
      setFilterModel((prev) => ({
        ...prev,
        filters: [
          ...prev.filters.filter((f) => f.identity !== filterIdentity && f.identity !== field),
          { identity: filterIdentity, matchMode: "in", value: values },
        ],
        first: 0,
        rows: pageSize,
      }));
    }

    debouncedCallback();
  };

  const handleTextfieldFilterChange = useCallback(
    (field: string, value: string) => {
      setValueDb((prev) => ({ ...prev, [field]: value }));

      if (value === "") {
        setFilterModel((prev) => ({
          ...prev,
          filters: prev.filters.filter((f) => f.identity !== field),
          first: 0,
          rows: pageSize,
        }));
      } else {
        setFilterModel((prev) => ({
          ...prev,
          filters: [
            ...prev.filters.filter((f) => f.identity !== field),
            { identity: field, matchMode: "contains", value: [value] },
          ],
          first: 0,
          rows: pageSize,
        }));
      }

      debouncedCallback();
    },
    [pageSize]
  );

  const getValues = (data: any[]): string[] => {
    try {
      return (
        data?.map((item) => {
          if (!item.value) {
            throw new Error(`Missing value in item: ${JSON.stringify(item)}`);
          }
          return item.value;
        }) || []
      );
    } catch (error) {
      console.error("Error extracting values:", error);
      return [];
    }
  };

  const handelPaginationModel = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    const updatedFilterModel = {
      ...filterModel,
      first: newPaginationModel.page * newPaginationModel.pageSize,
      rows: newPaginationModel.pageSize,
    };

    setFilterModel(updatedFilterModel);
    GetDashboardData(updatedFilterModel, viewTab);
    setPageSize(newPaginationModel.pageSize);
  };

  const handelSortModelChange = (sortModel) => {
    const updatedFilterModel = {
      ...filterModel,
      first: 0,
      sortOrder: sortModel[0]?.sort === "asc" ? 1 : -1,
      sortField: sortModel[0]?.field ? sortModel[0].field : "",
    };
    setPaginationModel({ page: 0, pageSize: pageSize });
    setFilterModel(updatedFilterModel);
    GetDashboardData(updatedFilterModel, viewTab);
  };

  const columns = useMemo(
    () =>
      [
        actionColumn,
        ...dashboardViewsColumns.coldDef1.filter(
          (col) => col.field !== "isActive"
        ),
      ].map((colDef: any) => {
        let { valueFormatter, type, ...rest } = colDef;

        if (colDef === actionColumn) {
          return rest;
        }
        if (
          [
            "leaseStatus",
            "investmentId",
            "investmentName",
            "propertyName",
            "leaseCommencement",
            "rentCommencement",
            "expiration",
            "nuveenSigningDate",
            "propertyManagerName",
            "propertyId",
            "secondaryPropertyManagerName",
          ].includes(colDef.field)
        ) {
          return {
            ...rest,
            renderHeaderFilter: (params: any) => (
              <CustomDropDownHeaderFilter
                {...params}
                dbFilters={dbFilters}
                onFilterChange={handleDropdownFilterChange}
                getValues={getValues}
                dbValues={valueDb}
              />
            ),
          };
        }

        return {
          ...rest,
          renderHeaderFilter: (params: any) => (
            <CustomTextHeaderFilter
              {...params}
              onFilterChange={handleTextfieldFilterChange}
              getValues={getValues}
            />
          ),
        };
      }),
    [dbFilters, handleTextfieldFilterChange]
  );

  const handleCsvExport = () => {
    try {
      const headers = columns
        .filter((col) => col.field !== "actions")
        .map((col) => col.headerName || col.field);
      const csvData = [
        headers,
        ...rowData.map((row) =>
          columns
            .filter((col) => col.field !== "actions")
            .map((col) => {
              const value = row[col.field];
              return value ? String(value) : "";
            })
        ),
      ];

      const csvContent = csvData
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");

      const now = new Date();
      const dateStr = now
        .toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "")
        .replace(",", "");
      const timeStr = now
        .toLocaleTimeString("en-US", { hour12: false })
        .replace(/:/g, "");
      const fileName = `Leases_export_${dateStr}_${timeStr}.csv`;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    } catch (error) {
      console.error("Error generating CSV:", error);
    }
  };

  const handlePdfExport = async () => {
    try {
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const headers = columns
        .filter((col) => col.field !== "actions")
        .map((col) => col.headerName || col.field);
      const data = rowData.map((row) =>
        columns
          .filter((col) => col.field !== "actions")
          .map((col) => {
            const value = row[col.field];
            return value ? String(value) : "";
          })
      );

      let yPosition = 40;
      const cellWidth = (pageWidth - 20) / headers.length;

      pdf.setFontSize(12);

      const headerLines = headers.map((header) =>
        pdf.splitTextToSize(header, cellWidth - 4)
      );
      const maxHeaderLines = Math.max(
        ...headerLines.map((lines) => lines.length)
      );
      const headerHeight = maxHeaderLines * 4 + 4;

      pdf.setFont("helvetica", "bold");
      headers.forEach((header, index) => {
        pdf.rect(10 + index * cellWidth, yPosition, cellWidth, headerHeight);
        const lines = headerLines[index];
        lines.forEach((line, lineIndex) => {
          pdf.text(
            line,
            10 + index * cellWidth + 2,
            yPosition + 5 + lineIndex * 4
          );
        });
      });
      pdf.setFont("helvetica", "normal");

      yPosition += headerHeight;

      data.forEach((row, rowIndex) => {
        const allLines = row.map((cell) =>
          pdf.splitTextToSize(cell, cellWidth - 4)
        );
        const maxLines = Math.max(...allLines.map((lines) => lines.length));
        const rowHeight = maxLines * 4 + 4;

        if (yPosition + rowHeight > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }

        row.forEach((cell, cellIndex) => {
          pdf.rect(10 + cellIndex * cellWidth, yPosition, cellWidth, rowHeight);
          const lines = allLines[cellIndex];
          lines.forEach((line, lineIndex) => {
            pdf.text(
              line,
              10 + cellIndex * cellWidth + 2,
              yPosition + 5 + lineIndex * 4
            );
          });
        });

        yPosition += rowHeight;
      });

      const now = new Date();
      const dateStr = now
        .toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "")
        .replace(",", "");
      const timeStr = now
        .toLocaleTimeString("en-US", { hour12: false })
        .replace(/:/g, "");
      const fileName = `Leases_export_${dateStr}_${timeStr}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.log("Error generating PDF:", error);
    }
  };

  const gridCustomToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          padding: 0.75,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListOffIcon />}
            sx={{
              textTransform: "none",
              borderColor: "#263746",
              color: "#263746",
              "&:hover": {
                borderColor: "#263746",
                backgroundColor: "rgba(38, 55, 70, 0.04)",
              },
            }}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            sx={{
              textTransform: "none",
              borderColor: "#263746",
              color: "#263746",
              "&:hover": {
                borderColor: "#263746",
                backgroundColor: "rgba(38, 55, 70, 0.04)",
              },
            }}
            onClick={handleCsvExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            sx={{
              textTransform: "none",
              borderColor: "#263746",
              color: "#263746",
              "&:hover": {
                borderColor: "#263746",
                backgroundColor: "rgba(38, 55, 70, 0.04)",
              },
            }}
            onClick={handlePdfExport}
          >
            Export PDF
          </Button>
        </Box>
      </GridToolbarContainer>
    );
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "calc(100% - 32px)",
          margin: "20px 15px 15px 15px",
          boxSizing: "border-box",
          height: "75vh",
          overflow: "hidden",
        }}
      >
        <Tabs
          sx={{
            minHeight: 40,
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTab-root": {
              fontSize: "14px",
              minHeight: 40,
              padding: "6px 12px",
              border: "1px solid #ccc",
              "&.Mui-selected": {
                backgroundColor: "#263746",
                color: "white",
              },
            },
          }}
          value={viewTab}
          onChange={handleTabChange}
        >
          <Tab
            label={
              userRole?.toLowerCase() === "accenture"
                ? "Accenture"
                : userRole?.toLowerCase() === "property manager"
                ? "My Leases In Progress"
                : "Leases In Progress"
            }
            id={"Leases In Progress"}
          />
          <Tab
            label={
              userRole?.toLowerCase() === "property manager"
                ? "My Active Leases"
                : "Active Leases"
            }
            id={"Active Leases"}
          />
        </Tabs>
        <div style={{ border: "1px solid #d5dce2ff", borderRadius: "0px 4px 4px 4px", padding: "8px" }}>
          <Grid item lg={12}>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: 2,
                my: 2,
              }}
            >
              {dbTopFiltersCount?.map((btn, i) => (
                <Button
                  key={i}
                  variant="contained"
                  fullWidth
                  style={{ backgroundColor: "#263746", color: "white" }}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    height: 40,
                    bgcolor: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                    fontSize: 12,
                  }}
                  onClick={() => buttonFilters(btn.label)}
                >
                  {`${btn.label} - ${btn.value}`}
                </Button>
              ))}
            </Box>
          </Grid>
          <Box
            sx={{
              width: "100%",
              height: "calc(75vh - 135px)",
              overflowX: "auto",
            }}
          >
            <DataGridPro
              headerFilters
              columns={columns}
              className="dashboard-dataGrid"
              rows={rowData || []}
              loading={loading}
              getRowId={(row) => row.leaseId}
              sortModel={sortModel}
              sx={{
                width: "100%",
                height: "100%",
                pointerEvents: loading ? "none" : "auto",
                opacity: loading ? 0.7 : 1,
                fontSize: "12px",
                // Add these styles for alternating rows
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5", // Light grey for odd rows
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff", // White for even rows
                },
                // Optional hover effect
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#e8f4f8", // Light blue on hover
                },
              }}
              slots={{
                loadingOverlay: GridLoadingOverlay,
                toolbar: gridCustomToolbar,
              }}
              rowCount={rowSize || 0}
              pagination
              paginationMode="server"
              sortingMode="server"
              columnHeaderHeight={36}
              headerFilterHeight={45}
              disableRowSelectionOnClick
              disableColumnMenu={true}
              hideFooter={false}
              onSortModelChange={(model) => {
                setSortModel(model);
                handelSortModelChange(model);
              }}
              paginationModel={paginationModel}
              onPaginationModelChange={handelPaginationModel}
              pageSizeOptions={[5, 25, 50, 100]}
            />
          </Box>
        </div>
      </Box>
    </React.Fragment>
  );
};

export default DashboardDataGrid;
